import { getEdgeMfaGateErrorMessage } from './edgeMfaGateErrorMessage';
import { isTransientError } from './errorHandler';
import { isBackdateServerError } from '../job/backdateServerError';
import { visitGuardMessage } from '../invoice/visitGuardErrors';
import { isSendingLimitError, rateLimitedMessage } from './rateLimit';
import { formatCurrency } from '../format/format';

/**
 * The ONE error translator (KAN-590 / RD-ERR-001). Users never see raw error
 * text: every action failure goes through here and gets a localized,
 * user-safe string. NEVER returns `err.message` / `details` / `hint`.
 *
 *   userSafeErrorMessage(err, intl, {
 *     fallbackId: 'services.saveError',        // action-specific, detail-free
 *     fallbackDefault: "Couldn't save the service. Try again.",
 *     codes: { MY_CODE: { id: <i18nId>, defaultMessage: '…' } },  // extra edge codes
 *   })
 *
 * `err` may be: an Error, a PostgREST/Postgres error ({code,message,details}),
 * a supabase `functions.invoke` error, a `callEdgeFunction` result
 * ({error,code,status}) or its bare `error` string/token, or a bare code string.
 * Order: MFA gate → network/transient → edge CODE → Postgres code → fallback.
 * The raw error is always console.warn'd and reported (nothing lost for debugging).
 */

const PERMISSION = { id: 'errors.code.permission', defaultMessage: "You don't have permission to do that." };
// KAN-200 Ship A: the company-access lock (PostgREST CVR01/CVR02 + edge 403 codes).
const COMPANY_READ_ONLY = { id: 'errors.code.COMPANY_READ_ONLY', defaultMessage: 'Your account is read-only. Open Plan & billing to subscribe.' };
const COMPANY_SUSPENDED = { id: 'errors.code.COMPANY_SUSPENDED', defaultMessage: 'Your account is suspended.' };
const PROTECTED = { id: 'errors.code.protectedAccount', defaultMessage: "This account is protected and can't be changed." };

export const EDGE_CODE_MESSAGES = {
  FORBIDDEN: PERMISSION,
  // 401 UNAUTHORIZED = missing/expired sign-in (usually a backgrounded PWA's
  // stale token), NOT a permission answer — never tell a user they lack rights.
  UNAUTHORIZED: { id: 'errors.code.UNAUTHORIZED', defaultMessage: 'Your sign-in needs a refresh. Try again.' },
  NOT_FOUND: { id: 'errors.code.NOT_FOUND', defaultMessage: 'This item no longer exists. Refresh and try again.' },
  RATE_LIMITED: { id: 'errors.code.RATE_LIMITED', defaultMessage: 'Too many tries. Wait a moment and try again.' },
  SEAT_LIMIT_REACHED: { id: 'errors.code.SEAT_LIMIT_REACHED', defaultMessage: 'Your plan has no free seats.' },
  COMPANY_READ_ONLY,
  COMPANY_SUSPENDED,
  TENANT_INACTIVE: { id: 'errors.code.TENANT_INACTIVE', defaultMessage: 'This company is inactive.' },
  // KAN-711: send-invoice-email — the caller's membership / role is no longer allowed to send (the run stops).
  MEMBER_INACTIVE: { id: 'errors.code.MEMBER_INACTIVE', defaultMessage: 'Your access to this company is inactive.' },
  ROLE_FORBIDDEN: PERMISSION,
  MEMBERSHIP_NOT_ACCEPTED: PERMISSION, // KAN-711: invite not accepted yet -> the generic permission copy
  DEFAULT_ADMIN_PROTECTED: PROTECTED,
  PROTECTED_USER: PROTECTED,
  EMAIL_NOT_INVITABLE: { id: 'errors.code.EMAIL_NOT_INVITABLE', defaultMessage: "This email can't be invited. Ask them to use a different email." },
  USER_IN_OTHER_COMPANY: { id: 'errors.code.USER_IN_OTHER_COMPANY', defaultMessage: "This email can't be added. Ask them to use a different email." },
  TARGET_NOT_MEMBER: { id: 'errors.code.TARGET_NOT_MEMBER', defaultMessage: "This person isn't an active member of your company." },
  TARGET_IN_OTHER_COMPANY: { id: 'errors.code.TARGET_IN_OTHER_COMPANY', defaultMessage: "This person also belongs to another company, so their sign-in security can't be changed here. Contact support." },
  TARGET_NOT_ELIGIBLE: { id: 'errors.code.TARGET_NOT_ELIGIBLE', defaultMessage: 'This person must be an active member who has accepted their invite.' },
  TARGET_OWNS_OTHER_COMPANY: { id: 'errors.code.TARGET_OWNS_OTHER_COMPANY', defaultMessage: 'This person already owns another company.' },
  PLATFORM_USER_PROTECTED: PROTECTED,
  LAST_ADMIN_PROTECTED: { id: 'errors.code.LAST_ADMIN_PROTECTED', defaultMessage: 'A company needs at least one admin.' },
  SELF_DEACTIVATION_BLOCKED: { id: 'errors.code.SELF_DEACTIVATION_BLOCKED', defaultMessage: "You can't deactivate your own account." },
  ALREADY_ACTIVE: { id: 'errors.code.ALREADY_ACTIVE', defaultMessage: 'This account is already active.' },
  ALREADY_INACTIVE: { id: 'errors.code.ALREADY_INACTIVE', defaultMessage: 'This account is already inactive.' },
  NO_PENDING_INVITE: { id: 'errors.code.NO_PENDING_INVITE', defaultMessage: 'This invite was already accepted or removed.' },
  AI_NOT_LICENSED: { id: 'errors.code.AI_NOT_LICENSED', defaultMessage: "AI isn't included in your plan." },
  PROTECTED_TARGET: { id: 'errors.code.PROTECTED_TARGET', defaultMessage: "You can't do that to this account." },
  // KAN-596: Platform edge codes (platform-* + manage-platform-invite).
  OWNER_PROTECTED: PROTECTED,
  INVALID_TARGET: { id: 'errors.code.INVALID_TARGET', defaultMessage: "You can't do that to this account." },
  REVOKE_FAILED: { id: 'errors.code.REVOKE_FAILED', defaultMessage: "Couldn't remove the invite. Try again." },
  REVOKE_INCOMPLETE: { id: 'errors.code.REVOKE_INCOMPLETE', defaultMessage: "The invite was removed, but cleanup didn't finish. Refresh to check." },
  TENANT_MUST_BE_INACTIVE: { id: 'errors.code.TENANT_MUST_BE_INACTIVE', defaultMessage: 'Deactivate this company first.' },
  STRIPE_SUBSCRIPTION_ACTIVE: { id: 'errors.code.STRIPE_SUBSCRIPTION_ACTIVE', defaultMessage: "Cancel this company's Stripe subscription first." },
  STRIPE_UNAVAILABLE: { id: 'errors.code.STRIPE_UNAVAILABLE', defaultMessage: "Couldn't check the subscription with Stripe. Try again." },
  STRIPE_LOOKUP_FAILED: { id: 'errors.code.STRIPE_LOOKUP_FAILED', defaultMessage: "Couldn't check the subscription. Try again." },
  USER_MUST_BE_INACTIVE: { id: 'errors.code.USER_MUST_BE_INACTIVE', defaultMessage: 'Deactivate this user first.' },
  MULTI_TENANT_USER: { id: 'errors.code.MULTI_TENANT_USER', defaultMessage: "This user also belongs to other companies, so they can't be deleted here." },
  USER_IN_USE: { id: 'errors.code.USER_IN_USE', defaultMessage: "This user still has records, so they can't be deleted." },
  EMAIL_ALREADY_EXISTS: { id: 'errors.code.EMAIL_ALREADY_EXISTS', defaultMessage: 'A user with this email already exists.' },
  TARGET_CHANGED: { id: 'errors.code.TARGET_CHANGED', defaultMessage: 'This account changed. Refresh and try again.' },
  // KAN-614: root-only + fresh-code company-member actions.
  ROOT_REQUIRED: { id: 'errors.code.ROOT_REQUIRED', defaultMessage: 'Only the root account can do this.' },
  MFA_STEP_UP_REQUIRED: { id: 'errors.code.MFA_STEP_UP_REQUIRED', defaultMessage: 'Enter a new code from your app to continue.' },
  ATTESTATION_REQUIRED: { id: 'errors.code.ATTESTATION_REQUIRED', defaultMessage: 'Confirm you verified this request first.' },
  SELF_RESET_FORBIDDEN: { id: 'errors.code.SELF_RESET_FORBIDDEN', defaultMessage: "You can't reset your own two-step verification here." },
  USER_HAS_HISTORY: { id: 'errors.code.USER_HAS_HISTORY', defaultMessage: "This person has work history, so they can't be deleted. Deactivate them instead." },
  AUTH_DELETE_FAILED: { id: 'errors.code.AUTH_DELETE_FAILED', defaultMessage: "Couldn't finish deleting the account. Try again." },
  MEMBER_REMOVE_FAILED: { id: 'errors.code.MEMBER_REMOVE_FAILED', defaultMessage: "Couldn't remove the member. Try again." },
  // RD-PLAT-016 / RD-SUB-015: plans, features and billing type refusals (platform-update-*, platform-create-tenant).
  DEPRECATED_FIELD: { id: 'errors.code.DEPRECATED_FIELD', defaultMessage: 'This option was retired. Update CrewVex, then try again.' },
  INVALID_BILLING_TYPE: { id: 'errors.code.INVALID_BILLING_TYPE', defaultMessage: 'Pick Free, Trial or Paying.' },
  INVALID_PAYING_STATUS: { id: 'errors.code.INVALID_PAYING_STATUS', defaultMessage: 'Pick Current, Past due or Cancelled.' },
  INVALID_TRIAL_DATE: { id: 'errors.code.INVALID_TRIAL_DATE', defaultMessage: 'Pick a valid trial end date.' },
  INVALID_TRIAL_DAYS: { id: 'errors.code.INVALID_TRIAL_DAYS', defaultMessage: 'Pick a 14-day or 30-day trial.' },
  TRIAL_DATE_REQUIRED: { id: 'errors.code.TRIAL_DATE_REQUIRED', defaultMessage: 'A trial needs an end date.' },
  TRIAL_DATE_IN_PAST: { id: 'errors.code.TRIAL_DATE_IN_PAST', defaultMessage: "The trial end date can't be before today." },
  BILLING_FIELD_MISMATCH: { id: 'errors.code.BILLING_FIELD_MISMATCH', defaultMessage: "That billing detail doesn't match the billing type. Check it and try again." },
  INVALID_AI_MONTHLY_CREDITS: { id: 'errors.code.INVALID_AI_MONTHLY_CREDITS', defaultMessage: "Enter a whole number of AI credits, or leave it empty to use the plan's amount." },
  INVALID_FEATURE_OVERRIDES: { id: 'errors.code.INVALID_FEATURE_OVERRIDES', defaultMessage: 'Pick Plan, On or Off for the feature.' },
  UNKNOWN_FEATURE: { id: 'errors.code.UNKNOWN_FEATURE', defaultMessage: 'That feature no longer exists. Refresh and try again.' },
  INVALID_ROLLOUT: { id: 'errors.code.INVALID_ROLLOUT', defaultMessage: 'Pick Off, Testing, By plan or Everyone.' },
  INVALID_PLAN_NAME: { id: 'errors.code.INVALID_PLAN_NAME', defaultMessage: 'A plan name is 1 to 60 characters.' },
  INVALID_PLAN_SEATS: { id: 'errors.code.INVALID_PLAN_SEATS', defaultMessage: 'Seats must be whole numbers.' },
  INVALID_PLAN_PRICE: { id: 'errors.code.INVALID_PLAN_PRICE', defaultMessage: 'Check the plan price: amounts can\'t be negative, a flat plan needs a price, a per-pool plan needs a rate, and the minimum can\'t be above the cap.' },
  INVALID_TRIAL_SETTINGS: { id: 'errors.code.INVALID_TRIAL_SETTINGS', defaultMessage: 'Check the trial: the length must be whole days, pick a plan for its seats, and AI credits must be a whole number.' },
  CUSTOM_PLAN_SEATLESS: { id: 'errors.code.CUSTOM_PLAN_SEATLESS', defaultMessage: 'The Custom plan has no seat defaults. Set seats on each company.' },
  CUSTOM_SEATS_REQUIRED: { id: 'errors.code.CUSTOM_SEATS_REQUIRED', defaultMessage: 'A custom plan needs both seat limits set.' },
  UNKNOWN_TENANT: { id: 'errors.code.UNKNOWN_TENANT', defaultMessage: "That company wasn't found. Refresh and try again." },
  UNKNOWN_PLAN: { id: 'errors.code.UNKNOWN_PLAN', defaultMessage: 'That plan no longer exists. Refresh and try again.' },
  INVALID_VALUE: { id: 'errors.code.INVALID_VALUE', defaultMessage: "One of the values isn't valid. Check the plan fields and try again." },
  INVALID_FIELD: { id: 'errors.code.INVALID_FIELD', defaultMessage: "One of the fields can't be changed here. Refresh and try again." },
  INVALID_OVERRIDE: { id: 'errors.code.INVALID_OVERRIDE', defaultMessage: 'Pick Plan, On or Off for the feature.' },
  // KAN-711: send-invoice-email 409 (and the merge compare-and-swap) when the reviewed total moved.
  TOTAL_CHANGED: { id: 'errors.code.TOTAL_CHANGED', defaultMessage: "This invoice's total changed after your review. Review it again before sending." },
  // VALIDATION_ERROR / DATA_FETCH_FAILED / UNEXPECTED_ERROR intentionally absent:
  // they fall through to the caller's action-specific fallback.
};


/**
 * KNOWN_SERVER_MESSAGES — the exact business sentences OUR OWN database
 * triggers / RPCs / edge functions raise on common user paths (grepped from
 * supabase/migrations + supabase/functions, no server change). They are matched
 * to a localized sentence so the user keeps the "why" without ever seeing the
 * server's text. First match wins; anything unknown falls to the action's
 * fallback. Add a row here (+ en/es keys) instead of interpolating `message`.
 */
const ALREADY_LOGGED = { id: 'errors.known.alreadyLogged', defaultMessage: "A job is already logged on that day, so this can't be changed. Delete that job first." };
export const KNOWN_SERVER_MESSAGES = [
  { re: /stop transferred/i, msg: { id: 'routeOverride.transferLocked.hint', defaultMessage: 'Transferred to another worker — cancel the transfer to reschedule or skip this visit.' } },
  { re: /locked on a sent or paid invoice/i, msg: { id: 'errors.known.billedJobLocked', defaultMessage: 'This job is on a sent or paid invoice, so its billing details are locked. Void the invoice first.' } },
  { re: /(a )?job has already been logged|already been logged|already logged|cannot undo this reschedule|cannot move this reschedule/i, msg: ALREADY_LOGGED },
  { re: /VISIT_REASON_REQUIRED|INVALID_VISIT_REASON/, msg: { id: 'errors.known.visitReasonRequired', defaultMessage: "For a closed pool, log the visit from 'Unscheduled Visit' and choose a reason." } },
  // KAN-1072: refusals when logging a visit (read from the LATEST migrations; pinned in userSafeError.logVisit.test.js).
  { re: /customer_inactive|cannot_log_unscheduled_for_inactive_customer/i, msg: { id: 'errors.known.customerInactive', defaultMessage: 'This customer is inactive. Reactivate them in Customers to log visits.' } },
  { re: /log an unscheduled visit instead/i, msg: { id: 'errors.known.scheduledDateOutsideWindow', defaultMessage: "This date is outside the scheduled visit's window. Log an unscheduled visit instead." } },
  { re: /worker is not eligible to log for this customer/i, msg: { id: 'errors.known.workerNotEligibleForCustomer', defaultMessage: "This customer isn't on your route for that date. Ask the office to assign it to you." } },
  { re: /jobs_visit_reason_required_for_unscheduled|jobs_visit_reason_check/i, msg: { id: 'errors.known.closedPoolVisitReason', defaultMessage: 'For a closed pool, choose a reason for the visit.' } },
  // Convert to recurring as Active without a start date (CHECK customers_service_start_date_active_required).
  { re: /customers_service_start_date_active_required/i, msg: { id: 'errors.known.customerStartDateRequired', defaultMessage: 'Pick the date this customer starts on the route, then try again.' } },
  { re: /customer not found/i, msg: { id: 'errors.known.customerNotFound', defaultMessage: 'Customer not found.' } },
  { re: /stop already (has a no-service|rescheduled)/i, msg: { id: 'errors.known.stopAlreadyHandled', defaultMessage: 'That stop already has a no-service or a reschedule on this date. Undo it first.' } },
  { re: /too far ahead|too far in the future|cannot be more than 90 days in the future/i, msg: { id: 'errors.known.tooFarAhead', defaultMessage: 'That date is too far ahead. Pick an earlier date.' } },
  { re: /too far in the past|too far back/i, msg: { id: 'errors.backdate.outsideWindow', defaultMessage: "That date is outside the window you're allowed to log or change. Pick a more recent date." } },
  { re: /cannot log jobs for future dates/i, msg: { id: 'errors.known.futureJob', defaultMessage: "You can't log a job for a future date." } },
  { re: /within the 48-hour window/i, msg: { id: 'errors.known.window48', defaultMessage: 'Workers can only log jobs from the last 48 hours.' } },
  { re: /illegal status transition|cannot change status of a deleted invoice/i, msg: { id: 'errors.known.invoiceStatusTransition', defaultMessage: "That status change isn't allowed for this invoice." } },
  { re: /only draft invoices can be deleted/i, msg: { id: 'errors.known.onlyDraftDelete', defaultMessage: 'Only draft invoices can be deleted. Void this one instead.' } },
  { re: /cannot void (invoice|deleted)|void reason/i, msg: { id: 'errors.known.cannotVoid', defaultMessage: "This invoice can't be voided right now." } },
  { re: /cannot be sent/i, msg: { id: 'errors.known.cannotSend', defaultMessage: "This invoice can't be sent in its current state." } },
  // KAN-1118: payment plans (set_estimate_payment_plan) + the estimate total floor (rpc_update_estimate).
  { re: /PAYMENT_PLAN_STATUS/, msg: { id: 'errors.known.paymentPlanStatus', defaultMessage: "This estimate can't be billed in payments right now. Refresh and try again." } },
  { re: /PAYMENT_PLAN_INVALID/, msg: { id: 'errors.known.paymentPlanInvalid', defaultMessage: 'Check the payment plan: each payment needs a percent, and they must leave something for the final payment.' } },
  { re: /PAYMENT_PLAN_BELOW_BILLED/, msg: { id: 'errors.known.paymentPlanBelowBilled', defaultMessage: 'The plan has fewer payments than are already billed. Refresh and try again.' } },
  { re: /PAYMENT_PLAN_BILLED_SLOT_FROZEN/, msg: { id: 'errors.known.paymentPlanSlotFrozen', defaultMessage: "A payment that is already billed can't be changed. Refresh and try again." } },
  { re: /invoices_estimate_sequence_cap_ck/, msg: { id: 'billing.estimate.convert.error.paymentCapReached', defaultMessage: 'This estimate already has 3 payment invoices. Void an invoice to bill that payment again.' } },
  { re: /estimate_total_invalid/, msg: { id: 'errors.known.estimateTotalInvalid', defaultMessage: 'Check the estimate amounts: each one must be a real number.' } },
  { re: /estimate_total_below_billed/, msg: { id: 'errors.known.estimateTotalBelowBilled', defaultMessage: "The total can't be lower than what is already billed on this estimate's invoices." } },
  { re: /COMPANY_READ_ONLY/, msg: COMPANY_READ_ONLY },
  { re: /COMPANY_SUSPENDED/, msg: COMPANY_SUSPENDED },
  { re: /does not have a pending invite/i, msg: EDGE_CODE_MESSAGES.NO_PENDING_INVITE },
  { re: /not[ _]authorized|unauthorized — tenant admin only|insufficient role|insufficient_privilege/i, msg: PERMISSION },
];

function serverText(err) {
  if (typeof err === 'string') return err;
  if (!err || typeof err !== 'object') return '';
  return [err.message, typeof err.error === 'string' ? err.error : err.error?.message, err.hint]
    .filter((x) => typeof x === 'string').join(' | ');
}

export function knownServerMessage(err) {
  if (isBackdateServerError(err)) return KNOWN_SERVER_MESSAGES.find((k) => k.msg.id === 'errors.backdate.outsideWindow').msg;
  const text = serverText(err);
  if (!text) return null;
  const hit = KNOWN_SERVER_MESSAGES.find((k) => k.re.test(text));
  return hit ? hit.msg : null;
}

/**
 * KAN-1061 (RD-BILL-020): the database money-cap trigger
 * (`private.assert_money_amount_cap`) raises 23514 with MESSAGE exactly
 * `amount_over_cap` and DETAIL = the cap as a plain number ("99999.99").
 * The DETAIL is only ever used after it parses as a bounded number, and is
 * re-formatted as currency — never shown as server text. A junk value (not a
 * number) raises 23514 `amount_invalid` with no DETAIL.
 */
export const AMOUNT_OVER_CAP = { id: 'errors.amountOverCap', defaultMessage: 'This amount is over the maximum ({max}).' };
export const AMOUNT_OVER_CAP_NO_MAX = { id: 'errors.amountOverCapNoMax', defaultMessage: 'This amount is over the maximum.' };
export const AMOUNT_INVALID = { id: 'errors.amountInvalid', defaultMessage: "This isn't a valid amount." };
const CAP_DETAIL = /^\d{1,9}(\.\d{1,2})?$/;

/** Also maps the trigger's sibling refusal `amount_invalid` (a price that isn't a number). */
export function amountOverCapMessage(err, intl) {
  const message = typeof err === 'string' ? err : (err && typeof err === 'object' ? err.message : null);
  if (typeof message !== 'string') return null;
  if (message.trim() === 'amount_invalid') {
    return intl.formatMessage({ id: AMOUNT_INVALID.id, defaultMessage: AMOUNT_INVALID.defaultMessage });
  }
  if (message.trim() !== 'amount_over_cap') return null;
  const detail = err && typeof err === 'object' && typeof err.details === 'string' ? err.details.trim() : '';
  if (CAP_DETAIL.test(detail)) {
    return intl.formatMessage(
      { id: AMOUNT_OVER_CAP.id, defaultMessage: AMOUNT_OVER_CAP.defaultMessage },
      { max: formatCurrency(Number(detail)) },
    );
  }
  return intl.formatMessage({ id: AMOUNT_OVER_CAP_NO_MAX.id, defaultMessage: AMOUNT_OVER_CAP_NO_MAX.defaultMessage });
}

export const IN_USE = { id: 'errors.inUse', defaultMessage: "This is still in use, so it can't be changed or removed." };

const PG_CODE_MESSAGES = {
  23505: { id: 'errors.duplicate', defaultMessage: 'That already exists.' },
  // 23503 (FK) is NOT mapped globally: log_job raises UNKNOWN_CATALOG_ITEM with
  // 23503, where "still in use" would be wrong. Delete paths opt in via
  // `codes: { 23503: IN_USE }` (exported below).
  42501: PERMISSION,
  CVR01: COMPANY_READ_ONLY,
  CVR02: COMPANY_SUSPENDED,
};

const asStr = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const CODE_SHAPE = /^[A-Z][A-Z0-9_]{2,}$/;

/**
 * Reads an edge-function error CODE from every shape in use: `err.code`,
 * `err.error.code` (`{error:{code,message}}` ApiError bodies), `err.context.code`
 * / `err.context.error.code`, `err.data|body|details` the same way, or a bare
 * CODE_LIKE string (callEdgeFunction hands back `error` tokens).
 */
export function extractErrorCode(err) {
  if (!err) return null;
  if (typeof err === 'string') return CODE_SHAPE.test(err.trim()) ? err.trim() : null;
  const probe = (o) => {
    if (!o || typeof o !== 'object') return null;
    return asStr(o.code) || asStr(o.error?.code) || null;
  };
  return probe(err)
    || probe(err.context) || probe(err.context?.body)
    || probe(err.data) || probe(err.body) || probe(err.details)
    || (typeof err.error === 'string' && CODE_SHAPE.test(err.error.trim()) ? err.error.trim() : null);
}

// Identical logs are dropped for 60s so render-time callers (AI cards) don't spam.
// Only `code` + a truncated message are logged: Sentry console breadcrumbs keep
// whatever object is passed, and PostgREST `details` can carry row values.
const LOG_DEDUPE_MS = 60000;
const recentLogs = new Map();
export function resetUserSafeErrorLogDedupe() { recentLogs.clear(); }

function logRaw(err) {
  try {
    const logCode = extractErrorCode(err) || err?.name || 'error';
    const logMsg = (asStr(err?.message) || asStr(err) || '').slice(0, 200);
    const key = `${logCode}|${logMsg}`;
    const now = Date.now();
    const seen = recentLogs.get(key);
    if (seen !== undefined && now - seen < LOG_DEDUPE_MS) return;
    if (recentLogs.size > 100) recentLogs.clear();
    recentLogs.set(key, now);
    console.warn(`[userSafeError] raw error hidden from user: ${logCode}: ${logMsg}`); // eslint-disable-line no-console
    import('./errorReporting')
      .then(({ reportError }) => {
        const code = extractErrorCode(err) || err?.name || 'error';
        reportError({ message: `[userSafeError] ${code}: ${asStr(err?.message) || asStr(err) || ''}`.slice(0, 500), stack: err?.stack || null }, { component: 'userSafeError' });
      })
      .catch(() => {});
  } catch { /* logging must never throw */ }
}

export function userSafeErrorMessage(err, intl, { fallbackId, fallbackDefault, codes } = {}) {
  const fmt = (m) => intl.formatMessage({ id: m.id, defaultMessage: m.defaultMessage });
  logRaw(err);

  const mfa = getEdgeMfaGateErrorMessage(err, intl);
  if (mfa) return mfa;

  // KAN-1061: the DB money cap (23514 amount_over_cap / amount_invalid).
  const overCap = amountOverCapMessage(err, intl);
  if (overCap) return overCap;

  // KAN-1087: a price-sighted caller left an extra's price blank (server refusal).
  const extraMsg = typeof err === 'string' ? err : (err && typeof err === 'object' ? err.message : null);
  if (typeof extraMsg === 'string' && extraMsg.trim() === 'EXTRA_PRICE_REQUIRED') {
    return intl.formatMessage(
      { id: 'jobs.unpricedExtras.pill', defaultMessage: '{n, plural, one {# extra needs a price} other {# extras need a price}}' },
      { n: 1 },
    );
  }

  // KAN-484: "one visit, one invoice" refusals (23505/23514/23503 would otherwise
  // read as "That already exists." / the generic fallback).
  const visitGuard = visitGuardMessage(err, intl);
  if (visitGuard) return visitGuard;

  // RD-SEC-061: the hourly sending limit (429 RATE_LIMITED + scope/retry) keeps its own sentence.
  if (isSendingLimitError(err)) return rateLimitedMessage(err, intl);

  const code = extractErrorCode(err);
  const msgs = (code && ((codes && codes[code]) || EDGE_CODE_MESSAGES[code])) || null;
  const network = intl.formatMessage({ id: 'errors.network', defaultMessage: 'Could not reach the server. Check your connection and try again.' });
  if (code === 'NETWORK_ERROR') return network;
  // A mapped code wins over the transient check (UNAUTHORIZED gets its own sign-in message).
  if (msgs) return fmt(msgs);
  const known = knownServerMessage(err);
  if (known) return fmt(known);
  if (isTransientError(err)) return network;

  const pg = err && typeof err === 'object' ? String(err.code ?? '') : '';
  if (pg && codes && codes[pg]) return fmt(codes[pg]);
  if (PG_CODE_MESSAGES[pg]) return fmt(PG_CODE_MESSAGES[pg]);

  if (fallbackId) {
    return intl.formatMessage({ id: fallbackId, defaultMessage: fallbackDefault });
  }
  return intl.formatMessage({ id: 'errors.unexpected', defaultMessage: 'Something went wrong. Please try again.' });
}

/**
 * Same contract for pure (non-React) modules that only receive an optional
 * `t(id, defaultMessage, values)` translator (bulk runners). No `t` = the
 * English defaults, so existing callers/tests keep working.
 */
export function userSafeErrorFromT(err, t, opts) {
  const intl = {
    formatMessage: (d, values) => (typeof t === 'function'
      ? t(d.id, d.defaultMessage, values)
      : Object.entries(values || {}).reduce((m, [k, v]) => m.replaceAll(`{${k}}`, String(v)), d.defaultMessage)),
  };
  return userSafeErrorMessage(err, intl, opts);
}
